import { useMemo } from "react";
import InputFormatter from "components/Records/Formatters/InputFormatter";
import { useSetAtom } from "jotai";
import { Input } from "reactstrap";
import { mappedHeadersAtom } from "./ImportDataWidget";
import ImportOptionsDropdown from "./ImportOptionsDropdown";
import produce from "immer";
import FieldUtils from "utils/FieldUtils";
import useModule from "hooks/useModule";
import { memo } from "react";
import { SelectPicker } from "rsuite";
import Constants from "utils/Constants";
import ActionsPicker from "./ActionsPicker";
import _ from "underscore";

const acceptableActions = ["record-create-update"];
const iconMap = Constants.MAPPING_ICONS;

const MappedHeader = ({
  mappedHeader,
  field,
  availableActions,
  parentIndex, // For nested header updates (Complex Field Updates)
  nestedField, // Also for nested header
  doubleNestedParent,
  doubleNestedIndex,
  parentRecord,
  dynamicValuesAvailable = false,
  usesActions,
  fileHeaders, // For when user has uploaded a file. Use headers from CSV/XLSX to prevent errors
  selectedHeaders, // Provided with fileHeaders.
}) => {
  const { module: parentRecordModule } = useModule(
    parentRecord?._ModuleAPIName
  );
  const setMappedHeaders = useSetAtom(mappedHeadersAtom);
  const actionMode = !!mappedHeader?.hasOwnProperty("ActionID");
  const valueMode = !!mappedHeader?.hasOwnProperty("Value");
  const identifierMode = !!mappedHeader?.hasOwnProperty("Identifier");
  const dynamicMode = !!mappedHeader?.hasOwnProperty("Dynamic");
  const isComplex = typeof parentIndex === "number" && nestedField;

  const dynamicValues = useMemo(() => {
    let generatedDynamicValues = [];
    if (!parentRecord) return generatedDynamicValues;

    generatedDynamicValues = Object.keys(parentRecord).flatMap(
      (recordFieldKey) => {
        const recordField = FieldUtils.getFieldFromModule(
          parentRecordModule,
          recordFieldKey
        );

        if (
          recordField?.FieldType === "MultiSubitem" ||
          recordFieldKey === "_id"
        ) {
          return [];
        }

        if (recordField?.FieldType === "Subitem") {
          return recordField.SubitemFields.map((subitemField) => {
            if (
              parentRecord[recordFieldKey][subitemField.APIName] &&
              !_.isEmpty(parentRecord[recordFieldKey][subitemField.APIName]) &&
              typeof parentRecord[recordFieldKey][subitemField.APIName] !==
                "object"
            ) {
              return {
                key: 1,
                label: `Record's ${subitemField.SubitemParent} - ${subitemField.Name}`,
                value: parentRecord[recordFieldKey][subitemField.APIName],
                type: "Subitem Options",
              };
            } else {
              return null;
            }
          });
        }

        if (
          parentRecord[recordFieldKey] &&
          !_.isEmpty(parentRecord[recordFieldKey]) &&
          typeof parentRecord[recordFieldKey] !== "object"
        ) {
          return {
            key: 2,
            label: `Record's ${recordFieldKey}`,
            value: parentRecord[recordFieldKey],
            type: "Record Options",
          };
        }
        return null;
      }
    );

    return generatedDynamicValues.filter(
      (dynamicValue) => dynamicValue !== null
    );
  }, [parentRecord, parentRecordModule]);

  return (
    <div className="d-flex" style={{ position: "relative" }}>
      {actionMode && (
        <ActionsPicker
          availableActions={availableActions}
          availableActionTypes={acceptableActions}
          onChange={(out) => handleValueChange(out?.ActionID ?? "")}
          value={mappedHeader?.ActionID ?? ""}
          width="100%"
          useRecordCreateUpdateOptions
          fieldType={nestedField ? nestedField?.FieldType : field?.FieldType}
        />
      )}
      {valueMode && (
        <div style={{ width: "100%" }}>
          <InputFormatter
            field={nestedField ? nestedField : field}
            value={mappedHeader?.Value ?? ""}
            onChange={(_, newValue) => handleValueChange(newValue)}
            placeholder={"Value"}
          />
        </div>
      )}
      {identifierMode && !fileHeaders && (
        <Input
          bsSize="sm"
          onChange={(e) => handleValueChange(e.target.value)}
          value={mappedHeader?.Identifier ?? ""}
          placeholder="Data Identifier"
        />
      )}
      {identifierMode && Array.isArray(fileHeaders) && (
        <Input
          bsSize="sm"
          type="select"
          onChange={(e) => handleValueChange(e.target.value)}
          value={mappedHeader?.Identifier ?? ""}
          placeholder="Data Identifier"
        >
          <option disabled value="">
            Select a value
          </option>
          {fileHeaders.map((header, i) => {
            const disabled = selectedHeaders?.includes?.(header);

            return (
              <option key={i} disabled={disabled} value={header}>
                {header}
              </option>
            );
          })}
        </Input>
      )}
      {dynamicMode && (
        <SelectPicker
          groupBy="type"
          placement="autoVertical"
          data={dynamicValues}
          style={{ width: "100%" }}
          placeholder="Choose A Dynamic Value"
          onChange={(val) => handleValueChange(val)}
          value={mappedHeader?.Dynamic ?? ""}
        />
      )}
      <div className="ml-2">
        <ImportOptionsDropdown
          onModeSwitch={onModeSwitch}
          onDeleteColumn={() => onDeleteColumn(field?.APIName)}
          dynamicValuesAvailable={dynamicValuesAvailable}
          usesActions={usesActions}
        />
      </div>
      <i
        className={
          iconMap[actionMode ? "Action" : valueMode ? "Value" : "Identifier"]
        }
        style={{ position: "absolute", left: -25, top: 1, fontSize: 22 }}
      />
    </div>
  );

  function handleValueChange(newVal, ignoreMode = false) {
    setMappedHeaders(
      produce((newHeaders) => {
        let updateValue;

        if (ignoreMode) {
          updateValue = newVal;
        } else if (identifierMode) {
          updateValue = { Identifier: newVal };
        } else if (valueMode) {
          updateValue = { Value: newVal };
        } else if (actionMode) {
          updateValue = { ActionID: newVal };
        } else if (dynamicMode) {
          updateValue = {
            Dynamic: newVal,
            ParentSource: parentRecord._ModuleAPIName,
          };
        }

        if (doubleNestedParent) {
          newHeaders[doubleNestedParent].Fields[doubleNestedIndex].Fields[
            field?.APIName
          ].Fields[parentIndex].Fields[nestedField?.APIName] = updateValue;
        } else if (isComplex) {
          newHeaders[field?.APIName].Fields[parentIndex].Fields[
            nestedField?.APIName
          ] = updateValue;
        } else {
          newHeaders[field?.APIName] = updateValue;
        }
      })
    );
  }

  function onDeleteColumn(header) {
    setMappedHeaders(
      produce((newHeaders) => {
        if (doubleNestedParent) {
          delete newHeaders[doubleNestedParent].Fields[doubleNestedIndex]
            .Fields[field?.APIName].Fields[parentIndex].Fields[
            nestedField?.APIName
          ];
        } else if (isComplex) {
          delete newHeaders[field?.APIName].Fields[parentIndex].Fields[
            nestedField?.APIName
          ];
        } else {
          delete newHeaders[header];
        }
      })
    );
  }

  function onModeSwitch(mode) {
    if (mode === "Identifier") {
      handleValueChange({ Identifier: "" }, true);
    }
    if (mode === "Action") {
      handleValueChange({ ActionID: "" }, true);
    }
    if (mode === "Value") {
      handleValueChange({ Value: "" }, true);
    }
    if (mode === "Dynamic") {
      handleValueChange({ Dynamic: "" }, true);
    }
  }
};

export default memo(MappedHeader);
